import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useRevealClientName } from "@/hooks/queries/useRevealClientName";
import { isGdprErasedError, toRevealNameErrorMessage } from "@/routes/clients/client-error";

// Shared masked-name + reveal-on-demand cell (Story 6.9, extracted from
// ClientsListScreen / Story 5.7). PHI-minimal by construction: a client's name is
// NEVER bulk-decrypted on a list. Each row shows the masked placeholder until the
// clinician explicitly reveals it; the reveal is a per-row mutation that audits
// server-side (one decrypt per deliberate click) and the decrypted name lives ONLY
// in this component's local state — never persisted, never logged.
//
// Reused by: the clients list Client column, the evaluations list Client column, and
// the workspace home in-progress + client-list sections. The component owns the
// masked → revealed → (terminal) erased rendering; the surrounding row owns any
// secondary identifier (the client UUID) and per-row actions.

/** Fixed, PHI-free name-cell copy for a GDPR-erased client (the name is exactly what was destroyed). */
export const GDPR_ERASED_LABEL = "Data erased — GDPR Art. 17";

/**
 * Format a GDPR Art. 17 erasure instant as a compact date, pinned to UTC. The
 * erasure timestamp is a medicolegal fact (ADR-002); rendering it in the browser's
 * local zone would shift the displayed date by a day for clinicians behind UTC, so
 * it is formatted in UTC for a stable audit date.
 */
export function formatGdprErasedDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** The terminal-tombstone tooltip/SR sentence (with the erasure date when known). */
export function gdprErasedTitle(erasedAt: string | null | undefined): string {
  const on = erasedAt ? formatGdprErasedDate(erasedAt) : null;
  return on
    ? `Permanently erased on ${on} under GDPR Art. 17 — personal data cannot be revealed.`
    : "Permanently erased under GDPR Art. 17 — personal data cannot be revealed.";
}

export interface ClientNameRevealProps {
  /** The client whose name is revealed on demand. */
  clientId: string;
  /** Terminal at load: the joined record carries a `gdpr_erased_at` stamp → render the tombstone. */
  initialErased?: boolean;
  /** Erasure instant (for the tombstone date), when known. */
  erasedAt?: string | null;
  /**
   * Notifies the parent when this row becomes terminally erased — either because it
   * loaded erased, or because a Reveal hit 410/ADMIN-004 mid-session (the erase
   * race). Lets the surrounding row drive its own terminal treatment (suppress the
   * per-row action, swap to an "Erased" badge) without duplicating the latch.
   */
  onErasedChange?: (erased: boolean) => void;
  /**
   * Notifies the parent of a successful reveal so it can client-side-filter by the
   * revealed name (the evaluations list search, AC 8). The name still lives ONLY in
   * component-local state for display; the parent mirrors it transiently for search and
   * must never persist or log it.
   */
  onRevealed?: (name: { first: string; last: string }) => void;
}

export function ClientNameReveal({
  clientId,
  initialErased = false,
  erasedAt = null,
  onErasedChange,
  onRevealed,
}: ClientNameRevealProps) {
  // Revealed name lives ONLY in local state — never logged, never persisted (PHI).
  const [revealed, setRevealed] = React.useState<{ first: string; last: string } | null>(null);
  const [revealError, setRevealError] = React.useState<string | null>(null);
  // A row erased mid-session (after load) surfaces only via a 410/ADMIN-004 on Reveal —
  // latch that so the row renders the SAME terminal state as a row already flagged erased.
  const [erasedByReveal, setErasedByReveal] = React.useState(false);

  const reveal = useRevealClientName();
  const isErased = initialErased || erasedByReveal;
  const title = gdprErasedTitle(erasedAt);

  const onReveal = () => {
    if (reveal.isPending) return;
    setRevealError(null);
    reveal.mutate(clientId, {
      onSuccess: (data) => {
        setRevealed({ first: data.first_name, last: data.last_name });
        onRevealed?.({ first: data.first_name, last: data.last_name });
      },
      onError: (error) => {
        // A GDPR-erased client is gone for good: switch to the terminal tombstone
        // (no retry), never the generic retryable message.
        if (isGdprErasedError(error)) {
          setErasedByReveal(true);
          setRevealError(null);
          onErasedChange?.(true);
        } else {
          setRevealError(toRevealNameErrorMessage());
        }
      },
    });
  };

  return (
    <>
      {/* Live region so the masked→revealed swap is announced — without it the reveal
          is a silent DOM change for screen-reader users (WCAG AA). */}
      <span className="inline-flex items-center gap-3" aria-live="polite" aria-atomic="true">
        {isErased ? (
          // Terminal state: the masked-name + Reveal cluster is REPLACED — there is
          // genuinely nothing to do, so nothing is clickable.
          <span
            className="inline-flex items-center gap-2 text-[color:var(--text-tertiary)]"
            title={title}
          >
            <EyeOff aria-hidden="true" className="h-4 w-4" />
            <span aria-hidden="true">{GDPR_ERASED_LABEL}</span>
            {/* `title=` is not reliably announced by assistive tech, so the full
                sentence (incl. the erasure date when known) is mirrored SR-only. */}
            <span className="sr-only">{title}</span>
          </span>
        ) : revealed ? (
          <span className="font-medium text-[color:var(--text-primary)]">
            {revealed.first} {revealed.last}
          </span>
        ) : (
          <>
            <span aria-hidden="true" className="text-[color:var(--text-quaternary)]">
              •••• ••••
            </span>
            <span className="sr-only">Name hidden</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={onReveal}
              disabled={reveal.isPending}
              aria-label={`Reveal client name (ID ${clientId})`}
            >
              <Eye aria-hidden="true" className="h-4 w-4" />
              {reveal.isPending ? "Revealing…" : "Reveal"}
            </Button>
          </>
        )}
      </span>
      {revealError ? (
        <p role="alert" className="mt-1 text-sm text-[color:var(--text-error)]">
          {revealError}
        </p>
      ) : null}
    </>
  );
}

export default ClientNameReveal;
